import React from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { withRouter } from "react-router";
import Typography from "@material-ui/core/Typography";

import NavBar from "../common/NavBar";

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: "100%",
        backgroundColor: theme.palette.background.default,
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        display: "flex",
        justifyContent: "center",
        " &::-webkit-scrollbar": {
            width: "12px",
            height: "10px",
            marginRight: "5px",
        },
        "&::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
            background: "#A0A0A0",
            borderRadius: "7px",
            backgroundClip: "padding-box",
            border: "3px solid transparent",
            minHeight: "50px",
            "&:hover": {
                background: "#A0A0A0",
                backgroundClip: "padding-box",
            },
        },
    },
    container: {
        display: "grid",
        gridTemplateColumns: "1fr",
        height: "100%",
        gap: "3em",
        padding: "40px",
        maxWidth: "1000px",
        width: "100%",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "7px",
        marginLeft: "30px",
        marginRight: "30px",
    },
}));

const Privacy = (props: any) => {
    const classes = useStyles();

    return (
        <div>
            <div>
                <NavBar />
            </div>
            <div className={classes.root}>
                <div className={classes.container}>
                    <div>
                        <Typography variant="h4" gutterBottom>
                            Privacy Policy
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            Revised: February 13th, 2026
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            Introduction
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Kibo is a product of{" "}
                            <a href="https://kalemicode.com" target="_blank">
                                <strong>Kalemi Code</strong>
                            </a>{" "}
                            ("Kalemi Code," "we," "us," or "our"), a software company headquartered in Tirana, Albania,
                            and incorporated in Wyoming, United States. Kalemi Code is the parent company and data
                            controller for Kibo, as well as our other products, including{" "}
                            <a href="https://publer.com" target="_blank">
                                Publer
                            </a>{" "}
                            and{" "}
                            <a href="https://linkie.bio" target="_blank">
                                Linkie
                            </a>
                            .
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            This Privacy Policy describes how we collect, use, disclose, and protect your personal
                            information when you use the Kibo website located at{" "}
                            <a href="https://kibo.ai" target="_blank">
                                kibo.ai
                            </a>{" "}
                            (the "Site") and related services (the "Service").
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            By using Kibo, you acknowledge that your information may be shared within the Kalemi Code
                            family of products as described in this Privacy Policy. For centralized privacy management,
                            including submitting data requests, viewing our list of sub-processors, and obtaining a copy
                            of our Data Processing Agreement, please visit the{" "}
                            <a href="https://kalemicode.com/privacy" target="_blank">
                                Kalemi Code Privacy Center
                            </a>
                            .
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            1. Scope of This Privacy Policy
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            This Privacy Policy applies to personal information collected through the Kibo Site and
                            Service. Because Kibo integrates with third-party communication channels and social
                            networks, information you provide to us or that we collect may also be accessible to those
                            providers and subject to their respective privacy policies. We do not control the privacy
                            practices of third-party platforms. We encourage you to review the privacy policies of any
                            platforms you connect to Kibo.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            2. Information We Collect
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            2.1 Information Collected Upon Registration
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            To access certain features of the Service, you must create a registered account. During
                            registration, we may collect the following personally identifiable information:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>Full name</li>
                                <li>Email address</li>
                                <li>Time zone</li>
                                <li>IP address</li>
                            </ul>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We do not store precise physical location.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            2.2 Payment Information
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We use third-party payment processors to handle payments. Kibo uses{" "}
                            <a href="https://polar.sh" target="_blank">
                                <strong>Polar</strong>
                            </a>{" "}
                            to process transactions. We do not store your full credit card number or payment details on
                            our servers. Payment information is collected and processed directly by Polar in accordance
                            with their privacy policy and PCI-DSS compliance standards.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            2.3 Information from Third-Party Platforms
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            When you connect your social media accounts or communication channels to Kibo, we may
                            receive information from those platforms in accordance with their terms and your privacy
                            settings. This may include account identifiers, profile information, and content you
                            authorize us to access.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            2.4 Automatically Collected Information
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We automatically collect certain information when you use the Site, including browser type,
                            device information, pages visited, and usage patterns. This information is collected through
                            cookies and similar technologies as described in Section 4.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            3. How We Use Your Information
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We use the information we collect for the following purposes:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    <strong>Providing the Service:</strong> To operate Kibo, manage your inboxes from
                                    connected social networks and communication channels, and manage your account.
                                </li>
                                <li>
                                    <strong>Communication:</strong> To send you service-related communications, respond
                                    to inquiries, and provide customer support.
                                </li>
                                <li>
                                    <strong>Marketing:</strong> To inform you about Kibo and other Kalemi Code products
                                    and services, including Publer and Linkie. You may opt out of marketing
                                    communications at any time by following the unsubscribe instructions in any
                                    communication.
                                </li>
                                <li>
                                    <strong>Improvement and Analytics:</strong> To analyze usage patterns, improve our
                                    Site and Service, and develop new features.
                                </li>
                                <li>
                                    <strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and
                                    legal processes.
                                </li>
                            </ul>
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            4. Cookies and Tracking Technologies
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We use cookies and similar technologies to collect usage data and improve your experience.
                            Cookies are small data files stored on your device.
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    <strong>Session Cookies:</strong> Enable certain features and are deleted when you
                                    close your browser.
                                </li>
                                <li>
                                    <strong>Persistent Cookies:</strong> Remain on your device and help us recognize you
                                    on subsequent visits.
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We do not use cookies to collect personally identifiable information. You may configure your
                            browser to reject cookies, though this may affect the functionality of the Site. For more
                            information, consult your browser's help documentation.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            5. Sharing of Information
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            5.1 Sharing with Third-Party Platforms
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            The primary purpose of Kibo is to allow you to manage the inboxes of the social networks and
                            other communication channels you have authenticated. Once you exchange messages with a
                            third-party platform, its use is governed by that platform's privacy policy.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            5.2 Sharing Within the Kalemi Code Family of Products
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Kibo is part of the Kalemi Code family of products, which also includes Publer and Linkie.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            <strong>
                                We may share your personal information, including your name, email address, account
                                information, and commercial information, with our affiliated products
                            </strong>{" "}
                            for the following purposes:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    To provide you with information about other Kalemi Code products and services that
                                    may be of interest to you
                                </li>
                                <li>To enable a unified customer experience across our product family</li>
                                <li>To advance our legitimate commercial and business interests</li>
                            </ul>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            If you prefer not to receive marketing communications from other Kalemi Code products, you
                            may opt out by following the unsubscribe instructions in any communication, submitting a{" "}
                            <a href="https://kalemicode.com/privacy/create-privacy-request" target="_blank">
                                privacy request
                            </a>
                            , or contacting us at <a href="mailto:privacy@kalemicode.com">privacy@kalemicode.com</a>.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            5.3 Sharing with Service Providers
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We engage third-party service providers to perform functions on our behalf, such as hosting,
                            analytics, payment processing, and customer support. These providers have access to your
                            personal information only as necessary to perform their services and are contractually
                            obligated to protect your information.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            For a complete list of our sub-processors, please visit our{" "}
                            <a href="https://kalemicode.com/privacy/policy/subprocessors" target="_blank">
                                Sub-processors List
                            </a>
                            .
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            5.4 Legal Disclosures
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We may disclose your information if required by law, regulation, legal process, or
                            governmental request, or if we believe disclosure is necessary to protect our rights, your
                            safety, or the safety of others.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            6. Data Retention
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We retain your personal information for as long as necessary to fulfill the purposes
                            described in this Privacy Policy, unless a longer retention period is required or permitted
                            by law.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            <strong>Account Deletion:</strong> You can permanently delete your account and associated
                            data at any time under your <a href="https://app.kibo.ai/settings">account settings</a>.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            7. Data Security
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We implement administrative, technical, and physical safeguards designed to protect your
                            personal information against unauthorized access, alteration, disclosure, or destruction.
                            These measures include encryption, access controls, and secure data storage.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            <strong>ISO/IEC 27001 Certification:</strong> Kalemi Code is ISO/IEC 27001 certified,
                            demonstrating our commitment to information security management best practices. You may
                            verify our certification{" "}
                            <a href="https://www.certipedia.com/certificates/01+153+2520565" target="_blank">
                                here
                            </a>
                            .
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            However, no method of transmission over the Internet or electronic storage is completely
                            secure. We cannot guarantee absolute security, and you transmit information to us at your
                            own risk.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            In the event of a data breach affecting your personal information, we will notify you in
                            accordance with applicable law.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            8. International Data Transfers
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Kibo is operated from servers located in Frankfurt, Germany. If you access the Service from
                            outside Germany, your information will be transferred to, processed, and stored in Germany.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            For users in the European Economic Area, United Kingdom, or Switzerland, we rely on
                            appropriate legal mechanisms to transfer personal information, including Standard
                            Contractual Clauses where applicable.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            By using the Service, you consent to the transfer of your information to Germany and other
                            jurisdictions where Kalemi Code or its service providers operate.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            9. Your Rights and Choices
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Depending on your location, you may have the following rights regarding your personal
                            information:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    <strong>Access:</strong> Request a copy of the personal information we hold about
                                    you.
                                </li>
                                <li>
                                    <strong>Correction:</strong> Request correction of inaccurate or incomplete
                                    information.
                                </li>
                                <li>
                                    <strong>Deletion:</strong> Request deletion of your personal information, subject to
                                    legal exceptions. You may also delete your account directly in your{" "}
                                    <a href="https://app.kibo.ai/settings">account settings</a>.
                                </li>
                                <li>
                                    <strong>Restriction:</strong> Request that we restrict processing of your
                                    information in certain circumstances.
                                </li>
                                <li>
                                    <strong>Portability:</strong> Request your information in a structured, commonly
                                    used, machine-readable format.
                                </li>
                                <li>
                                    <strong>Objection:</strong> Object to processing based on legitimate interests or
                                    for direct marketing purposes.
                                </li>
                                <li>
                                    <strong>Withdrawal of Consent:</strong> Where processing is based on consent,
                                    withdraw your consent at any time.
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            To exercise any of these rights, please submit a{" "}
                            <a href="https://kalemicode.com/privacy/create-privacy-request" target="_blank">
                                privacy request
                            </a>{" "}
                            or contact us at <a href="mailto:privacy@kalemicode.com">privacy@kalemicode.com</a>. We may
                            verify your identity before processing your request and will respond within the timeframes
                            required by applicable law.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            If you have connected social network accounts or communication channels to Kibo, you may
                            revoke our access at any time through your account settings or directly through the
                            platform's security settings.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            10. Children's Privacy
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Kibo is not intended for use by individuals under the age of 13 (or the applicable age of
                            consent in your jurisdiction). We do not knowingly collect personal information from
                            children. If we learn that we have collected personal information from a child, we will
                            promptly delete that information.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            If you believe a child has provided us with personal information, please submit a{" "}
                            <a href="https://kalemicode.com/privacy/create-privacy-request" target="_blank">
                                privacy request
                            </a>{" "}
                            or contact us at <a href="mailto:privacy@kalemicode.com">privacy@kalemicode.com</a>.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            11. Third-Party Links
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            The Site may contain links to third-party websites. We are not responsible for the privacy
                            practices of those websites. We encourage you to review the privacy policies of any
                            third-party sites you visit.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            12. Information for European Economic Area, United Kingdom, and Swiss Users (GDPR)
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            If you are located in the European Economic Area ("EEA"), United Kingdom ("UK"), or
                            Switzerland, the following additional information applies to you under the General Data
                            Protection Regulation ("GDPR") and equivalent laws.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            12.1 Data Controller
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Kalemi Code is the data controller responsible for your personal information. Our contact
                            details are provided in Section 15 of this Privacy Policy.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            12.2 Legal Bases for Processing
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We process your personal information on the following legal bases:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    <strong>Performance of a Contract:</strong> To provide the Service, manage your
                                    account, and fulfill our contractual obligations to you.
                                </li>
                                <li>
                                    <strong>Legitimate Interests:</strong> To improve our services, conduct analytics,
                                    prevent fraud, ensure security, and market our products to you, where such interests
                                    are not overridden by your data protection rights.
                                </li>
                                <li>
                                    <strong>Consent:</strong> Where you have provided consent for specific processing
                                    activities, such as receiving marketing communications or connecting third-party
                                    accounts. You may withdraw consent at any time.
                                </li>
                                <li>
                                    <strong>Legal Obligation:</strong> To comply with applicable laws, regulations, and
                                    legal processes.
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            12.3 Your Rights Under GDPR
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            You have the following rights regarding your personal information:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    <strong>Right of Access:</strong> Obtain confirmation of whether we process your
                                    personal data and request a copy of such data.
                                </li>
                                <li>
                                    <strong>Right to Rectification:</strong> Request correction of inaccurate or
                                    incomplete personal data.
                                </li>
                                <li>
                                    <strong>Right to Erasure:</strong> Request deletion of your personal data in certain
                                    circumstances. You may also delete your account directly in your{" "}
                                    <a href="https://app.kibo.ai/settings">account settings</a>.
                                </li>
                                <li>
                                    <strong>Right to Restriction:</strong> Request restriction of processing in certain
                                    circumstances.
                                </li>
                                <li>
                                    <strong>Right to Data Portability:</strong> Receive your personal data in a
                                    structured, commonly used, machine-readable format and transmit it to another
                                    controller.
                                </li>
                                <li>
                                    <strong>Right to Object:</strong> Object to processing based on legitimate interests
                                    or for direct marketing purposes.
                                </li>
                                <li>
                                    <strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where
                                    processing is based on consent.
                                </li>
                                <li>
                                    <strong>Right to Lodge a Complaint:</strong> Lodge a complaint with a supervisory
                                    authority in your country of residence.
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            To exercise these rights, please submit a{" "}
                            <a href="https://kalemicode.com/privacy/create-privacy-request" target="_blank">
                                privacy request
                            </a>{" "}
                            or contact us at <a href="mailto:privacy@kalemicode.com">privacy@kalemicode.com</a>.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            12.4 International Transfers
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            When we transfer personal data outside the EEA, UK, or Switzerland, we ensure appropriate
                            safeguards are in place, including:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>Standard Contractual Clauses approved by the European Commission</li>
                                <li>Transfers to countries with an adequacy decision</li>
                                <li>Other lawful transfer mechanisms as permitted under applicable law</li>
                            </ul>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            For more information about our international data transfer practices or to obtain a copy of
                            the relevant safeguards, please submit a{" "}
                            <a href="https://kalemicode.com/privacy/create-privacy-request" target="_blank">
                                privacy request
                            </a>{" "}
                            or contact us at <a href="mailto:privacy@kalemicode.com">privacy@kalemicode.com</a>.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            12.5 Data Processing Agreement
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            If you require a Data Processing Agreement for GDPR compliance, you may obtain one{" "}
                            <a href="https://kalemicode.com/privacy/policy/dpa" target="_blank">
                                here
                            </a>
                            .
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            12.6 EU Representative
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Pursuant to Article 27 of the General Data Protection Regulation (GDPR), Kalemi Code has
                            appointed the following representative in the European Union:
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            <strong>EU Presence d.o.o.</strong>
                            <br />
                            Ulica Brune Bušića 42
                            <br />
                            10000 Zagreb, Croatia
                            <br />
                            Email: dpo@eupresence.com
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            You may contact our EU Representative regarding any matters related to the processing of
                            your personal data under GDPR. You may verify our GDPR Representation Certificate{" "}
                            <a
                                href="https://kalemicode.com/privacy/certificate/61a2c8fc-8346-45c3-9000-3ad3f387debc"
                                target="_blank"
                            >
                                here
                            </a>
                            .
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            13. Information for California Residents (CCPA/CPRA)
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            If you are a California resident, the following additional information applies to you under
                            the California Consumer Privacy Act ("CCPA") as amended by the California Privacy Rights Act
                            ("CPRA").
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            13.1 Categories of Personal Information Collected
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            In the preceding 12 months, we have collected the following categories of personal
                            information:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    <strong>Identifiers:</strong> Name, email address, account name, IP address
                                </li>
                                <li>
                                    <strong>Commercial Information:</strong> Products or services purchased, usage
                                    history, payment transaction records (processed by{" "}
                                    <a href="https://polar.sh" target="_blank">
                                        Polar
                                    </a>
                                    )
                                </li>
                                <li>
                                    <strong>Internet or Network Activity:</strong> Browsing history, interactions with
                                    the Service
                                </li>
                                <li>
                                    <strong>Geolocation Data:</strong> Time zone (precise location is not collected)
                                </li>
                                <li>
                                    <strong>Professional or Employment Information:</strong> If provided in your profile
                                </li>
                                <li>
                                    <strong>Inferences:</strong> Preferences and characteristics derived from usage
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            13.2 Sources of Personal Information
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We collect personal information from:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>Directly from you when you register, use the Service, or communicate with us</li>
                                <li>Automatically through cookies and similar technologies</li>
                                <li>Third-party platforms you connect to the Service</li>
                            </ul>
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            13.3 Purposes for Collection and Use
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We collect and use personal information for the business and commercial purposes described
                            in Section 3 of this Privacy Policy, including providing the Service, communicating with
                            you, marketing, and improving our products.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            13.4 Disclosure and Sharing of Personal Information
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We disclose personal information to the following categories of recipients:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    <strong>Service Providers:</strong> Third parties who perform services on our behalf
                                </li>
                                <li>
                                    <strong>Affiliated Companies:</strong> Kalemi Code's family of products, including
                                    Publer and Linkie
                                </li>
                                <li>
                                    <strong>Third-Party Platforms:</strong> Platforms you authorize us to connect with
                                </li>
                                <li>
                                    <strong>Legal and Regulatory Authorities:</strong> When required by law
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            13.5 Sale and Sharing of Personal Information
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Kalemi Code does not "sell" personal information in exchange for monetary compensation.
                            However, under the CCPA/CPRA, sharing personal information for cross-context behavioral
                            advertising may be considered a "sale" or "share."
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            We may share your personal information with our affiliated products (Publer and Linkie) for
                            marketing purposes. If you wish to opt out of this sharing, you may do so by:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    Submitting a{" "}
                                    <a href="https://kalemicode.com/privacy/create-privacy-request" target="_blank">
                                        privacy request
                                    </a>
                                </li>
                                <li>
                                    Emailing us at <a href="mailto:privacy@kalemicode.com">privacy@kalemicode.com</a>
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            13.6 Your Rights Under CCPA/CPRA
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            As a California resident, you have the following rights:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    <strong>Right to Know:</strong> Request disclosure of the categories and specific
                                    pieces of personal information we have collected about you.
                                </li>
                                <li>
                                    <strong>Right to Delete:</strong> Request deletion of your personal information,
                                    subject to certain exceptions. You may also delete your account directly in your{" "}
                                    <a href="https://app.kibo.ai/settings">account settings</a>.
                                </li>
                                <li>
                                    <strong>Right to Correct:</strong> Request correction of inaccurate personal
                                    information.
                                </li>
                                <li>
                                    <strong>Right to Opt-Out:</strong> Opt out of the sale or sharing of your personal
                                    information.
                                </li>
                                <li>
                                    <strong>Right to Limit Use of Sensitive Personal Information:</strong> Limit the use
                                    of sensitive personal information, if applicable.
                                </li>
                                <li>
                                    <strong>Right to Non-Discrimination:</strong> We will not discriminate against you
                                    for exercising your privacy rights.
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            13.7 Submitting Requests
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            To exercise your rights, you or your authorized agent may:
                        </Typography>
                        <Typography variant="body1" component="div" gutterBottom>
                            <ul>
                                <li>
                                    Submit a{" "}
                                    <a href="https://kalemicode.com/privacy/create-privacy-request" target="_blank">
                                        privacy request
                                    </a>
                                </li>
                                <li>
                                    Email us at <a href="mailto:privacy@kalemicode.com">privacy@kalemicode.com</a>
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We will verify your identity before processing your request. We will respond to verifiable
                            requests within 45 days, or notify you if we require additional time (up to 90 days total).
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            13.8 Authorized Agents
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            You may designate an authorized agent to submit requests on your behalf. We may require
                            verification of the agent's authority and your identity before processing such requests.
                        </Typography>
                        <br />
                        <Typography variant="h6" gutterBottom>
                            13.9 Financial Incentives
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We do not offer financial incentives for the collection, sale, or deletion of personal
                            information.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            14. Changes to This Privacy Policy
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We may update this Privacy Policy from time to time. If we make material changes, we will
                            notify you by posting a prominent notice on the Site or by other appropriate means. Your
                            continued use of the Service after such changes constitutes your acceptance of the updated
                            Privacy Policy.
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            We encourage you to review this Privacy Policy periodically.
                        </Typography>
                    </div>

                    <div>
                        <Typography variant="h5" gutterBottom>
                            15. Contact Information
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Kalemi Code is the data controller responsible for your personal information. If you have
                            questions about this Privacy Policy or wish to exercise your privacy rights, please contact
                            us:
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            <strong>Kalemi Code</strong>
                            <br />
                            Rruga Abdyl Frasheri
                            <br />
                            Godina 26, Hyrja 1<br />
                            Tirana 1019
                            <br />
                            Albania
                            <br />
                            Registration Code: L71705026N
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            <strong>Kalemi Code LLC</strong>
                            <br />
                            1309 Coffeen Avenue, Ste 1200
                            <br />
                            Sheridan, WY 82801
                            <br />
                            United States
                            <br />
                            EIN: 88-0794373
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            Email: <a href="mailto:privacy@kalemicode.com">privacy@kalemicode.com</a>
                        </Typography>
                        <br />
                        <Typography variant="body1" gutterBottom>
                            For centralized privacy management, including data requests, sub-processor lists, and our
                            Data Processing Agreement, visit the{" "}
                            <a href="https://kalemicode.com/privacy" target="_blank">
                                Kalemi Code Privacy Center
                            </a>
                            .
                        </Typography>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withRouter(Privacy);
